import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Save, Trash2, Edit3, Plus, Minus, FileText, List, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateRubric, createRubric, getRubrics, updateRubric, deleteRubric, getAssignments } from '../../lib/api';
import { DraftyMascot } from '../DraftyMascot';
import { CCSS_STANDARDS } from './CreateAssignmentModal';
import { useAuth } from '../../lib/AuthContext';
import { toast } from 'sonner';
import { DemoChip } from '../ui/demo-chip';
import { parseRubricText, LEVEL_LABELS, LEVEL_SCORES } from '../../lib/rubricParser';
import { useRotatingStatus } from '../../lib/useRotatingStatus';

// Progress messaging for the rubric wait (R10). Steps describe the actual
// generation: criteria first, then the four performance levels per criterion.
const RUBRIC_WAIT_STEPS = [
  'Drafting the criteria…',
  'Writing the four levels for each one…',
  'Almost there. You can edit every word after this…',
];

const emptyLevel = () => LEVEL_SCORES.map((score, i) => ({
  score,
  label: LEVEL_LABELS[i],
  description: '',
}));

const emptyCriterion = () => ({
  criterion: '',
  levels: emptyLevel(),
});

// ===================== Sub-components =====================

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-slate-700 text-white'
        : 'text-slate-400 hover:text-white hover:bg-slate-800'
    }`}
    aria-current={active ? 'page' : undefined}
  >
    <Icon size={16} />
    {label}
  </button>
);

const CriteriaTable = ({ criteria, onChange, readOnly = false }) => {
  const updateCriterionName = (idx, name) => {
    const updated = [...criteria];
    updated[idx] = { ...updated[idx], criterion: name };
    onChange(updated);
  };

  const updateLevelDescription = (criterionIdx, levelIdx, description) => {
    const updated = [...criteria];
    const updatedLevels = [...updated[criterionIdx].levels];
    updatedLevels[levelIdx] = { ...updatedLevels[levelIdx], description };
    updated[criterionIdx] = { ...updated[criterionIdx], levels: updatedLevels };
    onChange(updated);
  };

  const addCriterion = () => {
    onChange([...criteria, emptyCriterion()]);
  };

  const removeCriterion = (idx) => {
    if (criteria.length <= 1) return;
    onChange(criteria.filter((_, i) => i !== idx));
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left text-slate-400 text-xs font-medium p-2 w-[160px]">Criterion</th>
            {LEVEL_SCORES.map((score, i) => (
              <th key={score} className="text-center text-xs font-medium p-2 min-w-[140px]">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                  score === 4 ? 'bg-emerald-900/50 text-emerald-400' :
                  score === 3 ? 'bg-teal-900/50 text-teal-400' :
                  score === 2 ? 'bg-amber-900/50 text-amber-400' :
                  'bg-red-900/50 text-red-400'
                }`}>
                  {score} - {LEVEL_LABELS[i]}
                </span>
              </th>
            ))}
            {!readOnly && <th className="w-[40px]" />}
          </tr>
        </thead>
        <tbody>
          {criteria.map((criterion, cIdx) => (
            <tr key={cIdx} className="border-t border-slate-700/50">
              <td className="p-2 align-top">
                {readOnly ? (
                  <span className="text-white text-sm font-medium">{criterion.criterion}</span>
                ) : (
                  <input
                    type="text"
                    value={criterion.criterion}
                    onChange={(e) => updateCriterionName(cIdx, e.target.value)}
                    placeholder="Criterion name"
                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-white text-sm"
                    aria-label={`Criterion ${cIdx + 1} name`}
                  />
                )}
              </td>
              {criterion.levels.map((level, lIdx) => (
                <td key={level.score} className="p-2 align-top">
                  {readOnly ? (
                    <span className="text-slate-300 text-xs">{level.description}</span>
                  ) : (
                    <textarea
                      value={level.description}
                      onChange={(e) => updateLevelDescription(cIdx, lIdx, e.target.value)}
                      placeholder={`${level.label} description...`}
                      rows={3}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-300 text-xs resize-y"
                      aria-label={`${criterion.criterion || `Criterion ${cIdx + 1}`} - ${level.label} description`}
                    />
                  )}
                </td>
              ))}
              {!readOnly && (
                <td className="p-2 align-top">
                  <button
                    onClick={() => removeCriterion(cIdx)}
                    disabled={criteria.length <= 1}
                    className="text-slate-500 hover:text-red-400 disabled:opacity-30 transition-colors p-1"
                    aria-label={`Remove criterion ${cIdx + 1}`}
                  >
                    <Minus size={14} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && (
        <button
          onClick={addCriterion}
          className="mt-2 flex items-center gap-1 text-teal-400 hover:text-teal-300 text-xs font-medium transition-colors"
        >
          <Plus size={14} />
          Add Criterion
        </button>
      )}
    </div>
  );
};

// ===================== Main Component =====================

export const FlowRubric = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');

  // Generate tab state
  const [selectedStandard, setSelectedStandard] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  // Progress messaging so a rubric wait is never a silent spinner (R10).
  // Declared above the JSX that reads it (D23).
  const rubricWaitLabel = useRotatingStatus(isGenerating, 'Generating…', RUBRIC_WAIT_STEPS);
  const [generatedCriteria, setGeneratedCriteria] = useState(null);
  const [rubricTitle, setRubricTitle] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // My Rubrics tab state
  const [savedRubrics, setSavedRubrics] = useState([]);
  const [isLoadingRubrics, setIsLoadingRubrics] = useState(false);
  const [editingRubric, setEditingRubric] = useState(null);
  const [editCriteria, setEditCriteria] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Shared state
  const [assignments, setAssignments] = useState([]);

  // Load assignments for the dropdown
  useEffect(() => {
    const loadAssignments = async () => {
      try {
        if (profile?.id) {
          const result = await getAssignments(profile.id);
          setAssignments(Array.isArray(result) ? result : []);
        }
      } catch {
        // Non-critical — dropdown will just be empty
      }
    };
    loadAssignments();
  }, [profile?.id]);

  // Load saved rubrics when switching to My Rubrics tab
  const loadRubrics = useCallback(async () => {
    setIsLoadingRubrics(true);
    try {
      const result = await getRubrics();
      setSavedRubrics(Array.isArray(result) ? result : []);
    } catch {
      toast.error('Failed to load rubrics');
    } finally {
      setIsLoadingRubrics(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'my-rubrics') {
      loadRubrics();
    }
  }, [activeTab, loadRubrics]);

  // ---- Generate tab handlers ----

  const handleGenerate = async () => {
    if (!selectedStandard) return;
    setIsGenerating(true);
    try {
      const result = await generateRubric(selectedStandard);
      const parsed = parseRubricText(result.rubric);
      setGeneratedCriteria(parsed);
      setRubricTitle(`${selectedStandard} Rubric`);
      toast.success('Rubric generated! Edit the criteria below.');
    } catch (error) {
      console.error('Rubric generation error:', error);
      const status = error.response?.status;
      toast.error(status === 502 || status === 503
        ? 'The AI service had trouble generating a rubric. Try again in a moment.'
        : 'Failed to generate rubric. Check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedCriteria || generatedCriteria.length === 0) return;
    if (!rubricTitle.trim()) {
      toast.error('Please enter a rubric title');
      return;
    }
    setIsSaving(true);
    try {
      await createRubric({
        standard_code: selectedStandard,
        title: rubricTitle.trim(),
        criteria: generatedCriteria,
        assignment_id: selectedAssignment || null,
        is_template: isTemplate,
      });
      toast.success('Rubric saved!');
      setGeneratedCriteria(null);
      setRubricTitle('');
      setSelectedAssignment('');
      setIsTemplate(false);
    } catch {
      toast.error('Failed to save rubric');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- My Rubrics tab handlers ----

  const handleEdit = (rubric) => {
    setEditingRubric(rubric.id);
    setEditCriteria(rubric.criteria ? [...rubric.criteria] : []);
    setEditTitle(rubric.title || '');
  };

  const handleCancelEdit = () => {
    setEditingRubric(null);
    setEditCriteria(null);
    setEditTitle('');
  };

  const handleUpdate = async () => {
    if (!editingRubric) return;
    setIsUpdating(true);
    try {
      await updateRubric(editingRubric, {
        title: editTitle.trim(),
        criteria: editCriteria,
      });
      toast.success('Rubric updated!');
      handleCancelEdit();
      loadRubrics();
    } catch {
      toast.error('Failed to update rubric');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (rubricId) => {
    try {
      await deleteRubric(rubricId);
      toast.success('Rubric deleted');
      setDeleteConfirm(null);
      setSavedRubrics(prev => prev.filter(r => r.id !== rubricId));
    } catch {
      toast.error('Failed to delete rubric');
    }
  };

  // ===================== Render =====================

  return (
    <div className="max-w-6xl mx-auto">
      <div className="card-surface p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <Sparkles className="text-ai-accent" size={28} />
            FlowRubric
          </h2>
          <div className="flex gap-2 bg-slate-800 rounded-lg p-1">
            <TabButton
              active={activeTab === 'generate'}
              onClick={() => setActiveTab('generate')}
              icon={Sparkles}
              label="Generate"
            />
            <TabButton
              active={activeTab === 'my-rubrics'}
              onClick={() => setActiveTab('my-rubrics')}
              icon={List}
              label="My Rubrics"
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* ==================== GENERATE TAB ==================== */}
          {activeTab === 'generate' && (
            <motion.div
              key="generate"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {/* Controls row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">CCSS Standard</label>
                  <select
                    data-testid="rubric-standard-select"
                    aria-label="Select CCSS standard for rubric generation"
                    value={selectedStandard}
                    onChange={(e) => setSelectedStandard(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="">Choose a standard...</option>
                    {CCSS_STANDARDS.map(std => (
                      <option key={std} value={std}>{std}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <DemoChip label="Sign in to generate rubrics">
                    <button
                      data-testid="generate-rubric-btn"
                      aria-label="Generate rubric"
                      onClick={handleGenerate}
                      disabled={!selectedStandard || isGenerating}
                      className="btn-purple w-full py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          <span aria-live="polite">{rubricWaitLabel}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={20} />
                          Generate Rubric
                        </>
                      )}
                    </button>
                  </DemoChip>
                </div>
                <div />
              </div>

              {/* Generated rubric editor */}
              {generatedCriteria ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* AI badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-ai-accent/20 text-ai-accent border border-ai-accent/30">
                      <Sparkles size={12} />
                      AI Generated
                    </span>
                    <span className="text-slate-500 text-xs">Edit any field below, then save</span>
                  </div>

                  {/* Title + assignment + template row */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-slate-400 text-xs mb-1">Rubric Title</label>
                      <input
                        type="text"
                        value={rubricTitle}
                        onChange={(e) => setRubricTitle(e.target.value)}
                        placeholder="Enter rubric title..."
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                        aria-label="Rubric title"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-1">Attach to Assignment</label>
                      <select
                        value={selectedAssignment}
                        onChange={(e) => setSelectedAssignment(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                        aria-label="Attach rubric to assignment"
                      >
                        <option value="">None (standalone)</option>
                        {assignments.map(a => (
                          <option key={a.id} value={a.id}>{a.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-3">
                      <label className="flex items-center gap-2 text-slate-400 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isTemplate}
                          onChange={(e) => setIsTemplate(e.target.checked)}
                          className="rounded border-slate-600"
                          aria-label="Save as template"
                        />
                        Save as template
                      </label>
                    </div>
                  </div>

                  {/* Editable criteria table */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 mb-4">
                    <CriteriaTable
                      criteria={generatedCriteria}
                      onChange={setGeneratedCriteria}
                    />
                  </div>

                  {/* Save button */}
                  <div className="flex justify-end">
                    <DemoChip label="Sign in to save rubric changes">
                      <button
                        onClick={handleSave}
                        disabled={isSaving || !rubricTitle.trim()}
                        className="btn-teal px-6 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={18} />
                            Save Rubric
                          </>
                        )}
                      </button>
                    </DemoChip>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-12 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
                    <p className="text-sm">Select a standard and click generate to create an editable rubric</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ==================== MY RUBRICS TAB ==================== */}
          {activeTab === 'my-rubrics' && (
            <motion.div
              key="my-rubrics"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {isLoadingRubrics ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="animate-spin text-slate-500" size={32} />
                </div>
              ) : savedRubrics.length === 0 ? (
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-12 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <FileText className="mx-auto mb-3 opacity-50" size={40} />
                    <p className="text-sm mb-1">No saved rubrics yet</p>
                    <p className="text-xs mb-4">Your saved rubrics will live here, ready to attach to any assignment.</p>
                    <button
                      data-testid="empty-generate-rubric"
                      onClick={() => setActiveTab('generate')}
                      className="btn-teal px-4 py-2 rounded-lg text-sm"
                    >
                      Generate your first rubric
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedRubrics.map((rubric) => (
                    <motion.div
                      key={rubric.id}
                      layout
                      className="bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden"
                    >
                      {/* Rubric header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
                        <div className="flex items-center gap-3">
                          {editingRubric === rubric.id ? (
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                              aria-label="Edit rubric title"
                            />
                          ) : (
                            <h3 className="text-white font-medium text-sm">{rubric.title}</h3>
                          )}
                          <span className="text-xs font-mono text-ai-accent bg-ai-accent/10 px-2 py-0.5 rounded">
                            {rubric.standard_code}
                          </span>
                          {rubric.is_template && (
                            <span className="text-xs text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded">
                              Template
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {editingRubric === rubric.id ? (
                            <>
                              <DemoChip label="Sign in to save rubric changes">
                                <button
                                  onClick={handleUpdate}
                                  disabled={isUpdating}
                                  className="btn-teal px-3 py-1.5 rounded text-xs flex items-center gap-1 disabled:opacity-50"
                                >
                                  {isUpdating ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                                  Update
                                </button>
                              </DemoChip>
                              <button
                                onClick={handleCancelEdit}
                                className="text-slate-400 hover:text-white text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(rubric)}
                                className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800 transition-colors"
                                aria-label={`Edit rubric: ${rubric.title}`}
                              >
                                <Edit3 size={14} />
                              </button>
                              {deleteConfirm === rubric.id ? (
                                <div className="flex items-center gap-1">
                                  <DemoChip label="Sign in to save rubric changes">
                                    <button
                                      onClick={() => handleDelete(rubric.id)}
                                      className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded bg-red-900/30 hover:bg-red-900/50 transition-colors flex items-center gap-1"
                                    >
                                      <AlertCircle size={12} />
                                      Confirm
                                    </button>
                                  </DemoChip>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="text-slate-400 hover:text-white text-xs px-2 py-1"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(rubric.id)}
                                  className="text-slate-400 hover:text-red-400 p-1.5 rounded hover:bg-slate-800 transition-colors"
                                  aria-label={`Delete rubric: ${rubric.title}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Rubric criteria table */}
                      <div className="p-4">
                        {editingRubric === rubric.id ? (
                          <CriteriaTable
                            criteria={editCriteria}
                            onChange={setEditCriteria}
                          />
                        ) : (
                          <CriteriaTable
                            criteria={rubric.criteria || []}
                            onChange={() => {}}
                            readOnly
                          />
                        )}
                      </div>

                      {/* Footer with metadata */}
                      <div className="px-4 py-2 bg-slate-800/30 border-t border-slate-700/30 flex items-center gap-4 text-xs text-slate-500">
                        <span>Created {new Date(rubric.created_at).toLocaleDateString()}</span>
                        {rubric.assignment_id && (
                          <span className="text-teal-500">Attached to assignment</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
